import { durationLabel } from "../../lib/rhythm/rhythmPathPlan";
import { musicNoteGlyph } from "../../lib/rhythm/musicGlyphs";

export interface RhythmFigureGlyphProps {
  /** Duración en beats (4 / 2 / 1 / 0.5). */
  durationBeats: number | null;
  isRest?: boolean;
  className?: string;
}

/**
 * Figura musical con tipografía Noto Music (gigante) + nombre + duración.
 */
export function RhythmFigureGlyph({
  durationBeats,
  isRest = false,
  className = "",
}: RhythmFigureGlyphProps) {
  const beats = durationBeats ?? 0;
  const name = isRest ? "Silencio" : beats > 0 ? durationLabel(beats) : "—";
  const timesLabel =
    beats > 0 ? `${beats} tiempo${beats === 1 ? "" : "s"}` : "";
  const glyph = beats > 0 || isRest ? musicNoteGlyph(Math.max(beats, 0.5), isRest) : "—";

  return (
    <div
      className={`flex flex-col items-center gap-1 ${className}`}
      aria-live="polite"
      aria-label={isRest ? "Silencio" : `${name}, ${timesLabel}`}
    >
      <span
        className={`font-music select-none leading-none ${
          isRest ? "text-amber-200/90" : "text-white/95"
        }`}
        style={{
          fontSize: "clamp(4.25rem, 11vw, 6.75rem)",
          textShadow: isRest
            ? "0 0 24px rgba(251,191,36,0.25)"
            : "0 0 28px rgba(153,246,228,0.2)",
        }}
        aria-hidden
      >
        {glyph}
      </span>
      <p className="font-serif text-2xl tracking-[0.08em] text-white/90 sm:text-3xl">
        {name.toUpperCase()}
      </p>
      {timesLabel ? (
        <p className="text-sm tabular-nums text-white/45">{timesLabel}</p>
      ) : null}
    </div>
  );
}

/** Mini grafía tipográfica para timeline (escala según duración). */
export function RhythmFigureMini({
  durationBeats,
  isRest = false,
  dimmed = false,
  active = false,
}: {
  durationBeats: number;
  isRest?: boolean;
  dimmed?: boolean;
  active?: boolean;
}) {
  const glyph = musicNoteGlyph(durationBeats, isRest);
  // Redonda más grande visualmente; corchea más compacta
  const size =
    durationBeats >= 3.5
      ? "text-[2.75rem]"
      : durationBeats >= 1.5
        ? "text-[2.35rem]"
        : durationBeats >= 0.75
          ? "text-[2.1rem]"
          : "text-[1.85rem]";

  return (
    <span
      className={`font-music select-none leading-none transition-opacity ${size} ${
        isRest ? "text-amber-200/85" : active ? "text-[#99f6e4]" : "text-white/90"
      }`}
      style={{ opacity: dimmed && !active ? 0.32 : 1 }}
      aria-hidden
    >
      {glyph}
    </span>
  );
}
